import React from 'react';
import { Alert, Box, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyInsightCard from './KeyInsightCard';
import SkeletonLoader from '../../../ui/primitives/SkeletonLoader';
import ActionButton from '../../../ui/primitives/ActionButton';

const FILTER_LABELS = {
  last_30_days: <>Executed in <Box component="span" sx={{ fontWeight: 700 }}>last 30 days</Box></>,
  last_90_days: <>Executed in <Box component="span" sx={{ fontWeight: 700 }}>last 90 days</Box></>,
  last_6_months: <>Executed in <Box component="span" sx={{ fontWeight: 700 }}>last 6 months</Box></>,
  last_year: <>Executed in <Box component="span" sx={{ fontWeight: 700 }}>last year</Box></>,
  custom: <>Executed in <Box component="span" sx={{ fontWeight: 700 }}>selected range</Box></>,
};

const CARD_CONFIG = [
  {
    key: 'total_test_cases',
    label: 'Total Test Cases',
    icon: <DescriptionOutlinedIcon />,
    paletteColor: 'primary',
    getSubtitle: (data, full) =>
      full?.total_test_suites?.count !== undefined
        ? `Across ${full.total_test_suites.count} Test Suites`
        : 'Total in repository',
  },
  {
    key: 'active_test_cases',
    label: 'Active Test Cases',
    icon: <TrendingUpIcon />,
    paletteColor: 'success',
    getSubtitle: (data) => FILTER_LABELS[data?.filter?.type] || FILTER_LABELS.custom,
  },
  {
    key: 'approved_test_cases',
    label: 'Approved Test Cases',
    icon: <VerifiedOutlinedIcon />,
    paletteColor: 'secondary',
    getSubtitle: () => '% of total test cases',
  },
  {
    key: 'orphaned_test_cases',
    label: 'Idle Test Cases',
    icon: <WarningAmberOutlinedIcon />,
    paletteColor: 'error',
    getSubtitle: () => 'Not executed at all',
  },
  {
    key: 'automation_coverage',
    label: 'Automation Coverage',
    icon: <SmartToyOutlinedIcon />,
    paletteColor: 'info',
    isPercentageValue: true,
    getSubtitle: () => 'Across all modules',
  },
  {
    key: 'requirements_traceability',
    label: 'Traceability',
    icon: <LinkOutlinedIcon />,
    paletteColor: 'warning',
    isPercentageValue: true,
    getSubtitle: () => `TC's linked to Jira/Conflunence`,
  },
];

const CARD_MIN_WIDTH = 240;
const SCROLL_STEP = 260;

const KeyInsightsSection = ({ data: insights, loading, isError, error, refetch }) => {
 const scrollRef = React.useRef(null);
  const handleScrollRight = () => {
    scrollRef.current?.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' });
  };

  if (loading) {
    return <SkeletonLoader count={CARD_CONFIG.length} />;
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={<ActionButton label="Retry" onClick={() => refetch()} size="small" />}
      >
        {error?.message || 'Failed to load key insights.'}
      </Alert>
    );
  }

  if (!insights) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={scrollRef}
        sx={(theme) => ({
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: theme.spacing(2),
          [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
          },
          [theme.breakpoints.down('sm')]: {
            display: 'flex',
            overflowX: 'auto',
            gridTemplateColumns: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            '& > *': {
              flex: `0 0 ${CARD_MIN_WIDTH}px`,
            },
          },
        })}
      >
        {CARD_CONFIG.map((config) => {
          const data = insights[config.key] || {};
          const value = config.isPercentageValue ? data.percentage : data.count;
          const valueSuffix = config.isPercentageValue ? '%' : '';
          const percentageLabel =
            !config.isPercentageValue && data.percentage !== undefined
              ? `${data.percentage}%`
              : undefined;

          return (
            <KeyInsightCard
              key={config.key}
              icon={config.icon}
              paletteColor={config.paletteColor}
              label={config.label}
              value={value}
              valueSuffix={valueSuffix}
              percentageLabel={percentageLabel}
              subtitle={config.getSubtitle(data, insights)}
            />
          );
        })}
      </Box>

      <Box
        sx={(theme) => ({
          display: 'none',
          [theme.breakpoints.down('sm')]: {
            display: 'flex',
          },
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          alignItems: 'center',
          pointerEvents: 'none',
          background: (theme) =>
            `linear-gradient(to right, ${alpha(theme.palette.background.default, 0)}, ${theme.palette.background.default} 70%)`,
          width: 48,
        })}
      >
        <IconButton
          size="small"
          onClick={handleScrollRight}
          sx={(theme) => ({
            pointerEvents: 'auto',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
          })}
        >
          <KeyboardArrowRightRoundedIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default KeyInsightsSection;